/**
 * «О чате» — `RoomDetailsStore` нативной Искры на `matrix-js-sdk`.
 *
 * Права читаются из power levels сервера один раз на перемену состояния и *прячут*
 * элементы, а не делают их серыми: серая кнопка — обещание, которое комната не сдержит.
 * Всё, что запрещено, просто не на экране. Пока состояния нет — запрещено всё: иначе
 * кнопка мигнула бы и исчезла.
 *
 * Роль — три слова для человека (администратор, модератор, участник). Вес — число, которое
 * сравнивает сервер: два администратора оба «администраторы», но тронуть друг друга не
 * могут, а создатель комнаты версии 12 выше любого числа (`Infinity`).
 */
import { Direction, EventType, MatrixEvent, Visibility, RoomEvent, RoomMemberEvent, RoomStateEvent, type MatrixClient, type Room } from 'matrix-js-sdk';
import { previewOf } from './preview';
import type { Preview } from './types';
import type { LocalNameSource } from './localNames.svelte.ts';
import { alertsRules, roomAlerts, type RoomAlerts } from './pushRules';

export type RoomRole = 'administrator' | 'moderator' | 'member';

export interface MemberSummary {
  id: string;
  /** Никогда не пустое: нет имени — Matrix ID. */
  name: string;
  avatarUrl?: string;
  role: RoomRole;
  /** Вес по правилам комнаты — только для сравнения, на экран не выводится. */
  authority: number;
  /** В комнате есть кто-то ещё с тем же именем — рядом показать Matrix ID. */
  ambiguous: boolean;
  invited: boolean;
}

/** Закреплённое — для списка «Закреплённые»: кто, когда и что. */
export interface PinnedSummary {
  eventId: string;
  senderId?: string;
  senderName?: string;
  ts?: number;
  /** Что в нём — словами, как в строке списка чатов; нет — ещё не загрузилось. */
  preview?: Preview;
  /** Достать не вышло: удалено или недоступно. */
  unavailable: boolean;
}

export interface RoomPermissions {
  canRename: boolean;
  canChangeTopic: boolean;
  canChangePicture: boolean;
  canChangeVisibility: boolean;
  canInvite: boolean;
  canRemove: boolean;
  canBan: boolean;
  canChangeRoles: boolean;
  canChangeHistory: boolean;
  canChangeAddress: boolean;
}

/**
 * Что из сказанного раньше видит пришедший позже — `HistoryAccess` нативной. «Всем, даже без
 * вступления» — только в открытой комнате.
 */
export type HistoryAccess = 'shared' | 'invited' | 'joined' | 'world_readable';
export const historyChoices = (open: boolean): HistoryAccess[] =>
  open ? ['shared', 'invited', 'joined', 'world_readable'] : ['shared', 'invited', 'joined'];

const DENIED: RoomPermissions = {
  canRename: false,
  canChangeTopic: false,
  canChangePicture: false,
  canChangeVisibility: false,
  canInvite: false,
  canRemove: false,
  canBan: false,
  canChangeRoles: false,
  canChangeHistory: false,
  canChangeAddress: false,
};

/** Что не получилось — ключи текстов нативной Искры (`roomAction.*Failed`). */
export type RoomActionFailure =
  | 'rename'
  | 'topic'
  | 'picture'
  | 'visibility'
  | 'invite'
  | 'remove'
  | 'ban'
  | 'unban'
  | 'role'
  | 'leave'
  | 'alerts'
  | 'history'
  | 'address'
  | 'addressTaken'
  | 'directory'
  | 'rules';

/** Уровни ролей — как их пишут Element и нативная Искра. */
export const ROLE_LEVEL: Record<RoomRole, number> = { administrator: 100, moderator: 50, member: 0 };

/**
 * Что в комнате кому можно — `RoomRule` нативной: восемь порогов `m.room.power_levels`, каждый
 * показан нижней ступенью, которой можно: все, модераторы, администраторы.
 */
export const roomRules = ['sendMessages', 'invite', 'remove', 'ban', 'deleteOthers', 'rename', 'changeTopic', 'changePicture'] as const;
export type RoomRule = (typeof roomRules)[number];

/** Где порог правила живёт в power levels и чему он равен, если его нет (спецификация). */
const RULE_LEVELS: Record<RoomRule, { key: string; event?: string; fallback: (powers: Record<string, unknown>) => number }> = {
  sendMessages: { key: 'events_default', fallback: () => 0 },
  invite: { key: 'invite', fallback: () => 0 },
  remove: { key: 'kick', fallback: () => 50 },
  ban: { key: 'ban', fallback: () => 50 },
  deleteOthers: { key: 'redact', fallback: () => 50 },
  rename: { key: 'events', event: EventType.RoomName, fallback: stateDefault },
  changeTopic: { key: 'events', event: EventType.RoomTopic, fallback: stateDefault },
  changePicture: { key: 'events', event: EventType.RoomAvatar, fallback: stateDefault },
};

function stateDefault(powers: Record<string, unknown>): number {
  return typeof powers['state_default'] === 'number' ? powers['state_default'] : 50;
}

function ruleLevel(powers: Record<string, unknown>, rule: RoomRule): number {
  const { key, event, fallback } = RULE_LEVELS[rule];
  const value = event ? ((powers['events'] ?? {}) as Record<string, unknown>)[event] : powers[key];
  return typeof value === 'number' ? value : fallback(powers);
}

const roleAt = (level: number): RoomRole =>
  level >= ROLE_LEVEL.administrator ? 'administrator' : level >= ROLE_LEVEL.moderator ? 'moderator' : 'member';

/** Версии комнат, где создатель — вне чисел (MSC4289). */
const CREATOR_IS_UNLIMITED = new Set(['12', 'org.matrix.hydra.11']);

/**
 * Что можно сделать адресом из набранного: строчные, дефисы вместо пробелов, ничего, на чём
 * споткнётся ссылка. Набранные целиком `#имя:сервер` прощаются — адреса вставляют.
 */
export function addressName(typed: string): string {
  let text = typed.trim();
  if (text.startsWith('#')) text = text.slice(1);
  const colon = text.indexOf(':');
  if (colon >= 0) text = text.slice(0, colon);
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\p{L}\p{N}._=-]/gu, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export class RoomDetailsStore {
  /** Что на экране: локальное имя личного чата, если оно есть, иначе — `serverName`. */
  name = $state('');
  /** Как чат называет сервер: собеседник или собственное имя комнаты. */
  serverName = $state('');
  /** Как *я* зову собеседника на этом устройстве (`LocalNames`); только личный чат. */
  localName = $state<string | undefined>(undefined);
  topic = $state('');
  avatarUrl = $state<string | undefined>(undefined);
  direct = $state(false);
  encrypted = $state(false);
  /** Открытый: войти может любой, кто найдёт. */
  open = $state(false);
  members = $state.raw<MemberSummary[]>([]);
  /** Кого сюда не пускают — для тех, кто может пустить обратно (`unban`). */
  banned = $state.raw<MemberSummary[]>([]);
  history = $state<HistoryAccess>('shared');
  /** Главный адрес комнаты, `#имя:сервер`; нет — нет. */
  alias = $state<string | undefined>(undefined);
  /** Кому что можно — нижней ступенью; `null` — power levels ещё не пришли. */
  rules = $state.raw<Record<RoomRule, RoomRole> | null>(null);
  /** Порог каждого правила числом — чтобы знать, можно ли его тронуть. */
  private ruleLevels: Record<string, number> = {};
  private powers: Record<string, unknown> = {};
  /** В каталоге сервера? Знает только сервер, и спрашивают его только из настроек: `undefined`. */
  inDirectory = $state<boolean | undefined>(undefined);
  permissions = $state.raw<RoomPermissions>(DENIED);
  alerts = $state<RoomAlerts>('all');
  /** Закреплённые — последнее закреплённое первым. */
  pinned = $state.raw<PinnedSummary[]>([]);
  /** Мой вес — с ним сравнивается каждое «можно ли тронуть этого человека». */
  ownAuthority = $state(0);
  working = $state(false);
  failure = $state<RoomActionFailure | null>(null);

  private readonly me: string;
  private readonly detach: Array<() => void> = [];
  private membersLoaded = false;
  // Кэш достатого по одному, не состояние: экран видит `pinned`.
  // eslint-disable-next-line svelte/prefer-svelte-reactivity
  private readonly fetched = new Map<string, MatrixEvent | null>();

  constructor(
    private readonly client: MatrixClient,
    readonly roomId: string,
    private readonly isDirect: (roomId: string) => boolean,
    private readonly localNames?: LocalNameSource & { set(roomId: string, name: string | null): Promise<void> },
  ) {
    this.me = client.getSafeUserId();
    if (localNames) this.detach.push(localNames.onChange((changed) => (changed === null || changed === roomId) && this.read()));
    const on = (event: string, handler: (...args: never[]) => void) => {
      const target = client as unknown as { on(e: string, h: (...a: never[]) => void): void; off(e: string, h: (...a: never[]) => void): void };
      target.on(event, handler);
      this.detach.push(() => target.off(event, handler));
    };
    const mine = (e: MatrixEvent) => e.getRoomId() === roomId;
    // Имя комнаты SDK пересчитывает *после* события о состоянии — поэтому и его пересчёт.
    on(RoomStateEvent.Events, (e: MatrixEvent) => mine(e) && this.read());
    on(RoomEvent.Name, (room: Room) => room.roomId === roomId && this.read());
    on(RoomMemberEvent.Name, () => this.read());
    on(RoomMemberEvent.Membership, () => this.read());
    on('accountData', (e: MatrixEvent) => e.getType() === EventType.PushRules && this.readAlerts());
    this.read();
    void this.loadMembers();
  }

  private get room(): Room | null {
    return this.client.getRoom(this.roomId);
  }

  /** Полный список участников — лениво: в первой синхронизации его может не быть. */
  private async loadMembers(): Promise<void> {
    try {
      await this.room?.loadMembersIfNeeded();
    } catch {
      // Не пришёл — покажем тех, кого знаем.
    }
    this.membersLoaded = true;
    this.read();
  }

  read(): void {
    const room = this.room;
    if (!room) return;
    const state = room.getLiveTimeline().getState(Direction.Forward)!;
    this.direct = this.isDirect(this.roomId);
    this.serverName = room.name;
    this.localName = this.direct ? this.localNames?.get(this.roomId) : undefined;
    this.name = this.localName ?? room.name;
    this.topic = (state.getStateEvents(EventType.RoomTopic, '')?.getContent()['topic'] as string | undefined) ?? '';
    this.avatarUrl = room.getMxcAvatarUrl() ?? undefined;
    this.encrypted = room.hasEncryptionStateEvent();
    this.open = room.getJoinRule() === 'public';

    const powers = state.getStateEvents(EventType.RoomPowerLevels, '')?.getContent() ?? {};
    const users = (powers['users'] ?? {}) as Record<string, number>;
    const usersDefault = typeof powers['users_default'] === 'number' ? powers['users_default'] : 0;
    const create = state.getStateEvents(EventType.RoomCreate, '');
    // Снимки на одно чтение, не состояние.
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const creators = new Set<string>();
    if (create && CREATOR_IS_UNLIMITED.has(String(create.getContent()['room_version'] ?? ''))) {
      const sender = create.getSender();
      if (sender) creators.add(sender);
      for (const extra of (create.getContent()['additional_creators'] as string[] | undefined) ?? []) creators.add(extra);
    }
    const authorityOf = (userId: string) => (creators.has(userId) ? Infinity : typeof users[userId] === 'number' ? users[userId]! : usersDefault);
    const roleOf = (authority: number): RoomRole =>
      authority >= ROLE_LEVEL.administrator ? 'administrator' : authority >= ROLE_LEVEL.moderator ? 'moderator' : 'member';

    const people = room.getMembers().filter((m) => m.membership === 'join' || m.membership === 'invite');
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const names = new Map<string, number>();
    for (const m of people) names.set(m.name, (names.get(m.name) ?? 0) + 1);
    const rank: Record<RoomRole, number> = { administrator: 0, moderator: 1, member: 2 };
    this.members = people
      .map((m): MemberSummary => {
        const authority = authorityOf(m.userId);
        return {
          id: m.userId,
          name: m.name || m.userId,
          ...(m.getMxcAvatarUrl() ? { avatarUrl: m.getMxcAvatarUrl()! } : {}),
          role: roleOf(authority),
          authority,
          ambiguous: (names.get(m.name) ?? 0) > 1 || m.disambiguate,
          invited: m.membership === 'invite',
        };
      })
      // Администраторы, модераторы, остальные; внутри — по имени. Приглашённые — в конце.
      .sort((a, b) => +a.invited - +b.invited || rank[a.role] - rank[b.role] || a.name.localeCompare(b.name));

    this.banned = room
      .getMembers()
      .filter((m) => m.membership === 'ban')
      .map((m): MemberSummary => {
        const authority = authorityOf(m.userId);
        // В событии блокировки имени нет — человек назван так, как его знали в чате до неё.
        const before = m.events.member?.getPrevContent()['displayname'];
        const name = m.name && m.name !== m.userId ? m.name : typeof before === 'string' && before ? before : m.userId;
        return { id: m.userId, name, role: roleOf(authority), authority, ambiguous: m.disambiguate, invited: false };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    const visibility = state.getStateEvents(EventType.RoomHistoryVisibility, '')?.getContent()['history_visibility'];
    // Своё расширение сервера описать нечем — как умолчание спецификации, «вся история».
    this.history = visibility === 'invited' || visibility === 'joined' || visibility === 'world_readable' ? visibility : 'shared';
    const alias = state.getStateEvents(EventType.RoomCanonicalAlias, '')?.getContent()['alias'];
    this.alias = typeof alias === 'string' ? alias : undefined;

    this.powers = powers;
    this.ruleLevels = Object.fromEntries(roomRules.map((rule) => [rule, ruleLevel(powers, rule)]));
    this.rules = Object.fromEntries(roomRules.map((rule) => [rule, roleAt(this.ruleLevels[rule]!)])) as Record<RoomRule, RoomRole>;

    this.ownAuthority = authorityOf(this.me);
    const level = (key: string, fallback: number) => (typeof powers[key] === 'number' ? (powers[key] as number) : fallback);
    const may = (type: string) => state.maySendStateEvent(type, this.me);
    this.permissions = {
      canRename: may(EventType.RoomName),
      canChangeTopic: may(EventType.RoomTopic),
      canChangePicture: may(EventType.RoomAvatar),
      canChangeVisibility: may(EventType.RoomJoinRules) && !this.direct,
      canInvite: this.ownAuthority >= level('invite', 0) && room.getMyMembership() === 'join',
      canRemove: this.ownAuthority >= level('kick', 50),
      canBan: this.ownAuthority >= level('ban', 50),
      canChangeRoles: may(EventType.RoomPowerLevels),
      canChangeHistory: may(EventType.RoomHistoryVisibility) && !this.direct,
      canChangeAddress: may(EventType.RoomCanonicalAlias) && !this.direct,
    };
    this.readAlerts();
    this.readPinned(room, state.getStateEvents('m.room.pinned_events', '')?.getContent()['pinned']);
  }

  private readPinned(room: Room, raw: unknown): void {
    const ids = (Array.isArray(raw) ? raw : []).filter((id): id is string => typeof id === 'string').reverse();
    this.pinned = ids.map((eventId): PinnedSummary => {
      const event = room.findEventById(eventId) ?? this.fetched.get(eventId) ?? undefined;
      if (event === undefined && !this.fetched.has(eventId)) void this.fetchPinned(eventId);
      if (!event) return { eventId, unavailable: this.fetched.get(eventId) === null };
      const sender = event.getSender();
      const preview = event.isRedacted() ? undefined : previewOf(event);
      return {
        eventId,
        ...(sender ? { senderId: sender, senderName: room.getMember(sender)?.name || sender } : {}),
        ts: event.getTs(),
        ...(preview ? { preview } : {}),
        unavailable: event.isRedacted(),
      };
    });
  }

  private async fetchPinned(eventId: string): Promise<void> {
    this.fetched.set(eventId, null);
    try {
      const event = new MatrixEvent(await this.client.fetchRoomEvent(this.roomId, eventId));
      await this.client.decryptEventIfNeeded(event);
      this.fetched.set(eventId, event);
    } catch {
      // Удалено или не видно этому аккаунту — «недоступно».
    }
    this.read();
  }

  private readAlerts(): void {
    this.alerts = roomAlerts(this.client.pushRules, this.roomId);
  }

  /** Тронуть можно только того, кто ниже тебя, и не себя: уйти — другая кнопка. */
  mayManage(member: MemberSummary): boolean {
    return member.id !== this.me && member.authority < this.ownAuthority;
  }

  mayRemove(member: MemberSummary): boolean {
    return this.permissions.canRemove && this.mayManage(member);
  }

  mayBan(member: MemberSummary): boolean {
    return this.permissions.canBan && this.mayManage(member);
  }

  /** Роли, которые я могу дать: не выше своей. */
  rolesFor(member: MemberSummary): RoomRole[] {
    if (!this.permissions.canChangeRoles || !this.mayManage(member)) return [];
    return (['administrator', 'moderator', 'member'] as const).filter((role) => ROLE_LEVEL[role] <= this.ownAuthority);
  }

  get membersReady(): boolean {
    return this.membersLoaded;
  }

  // ————— Действия —————

  private async act(failure: RoomActionFailure, task: () => Promise<unknown>): Promise<boolean> {
    this.working = true;
    this.failure = null;
    try {
      await task();
      return true;
    } catch {
      this.failure = failure;
      this.read();
      return false;
    } finally {
      this.working = false;
    }
  }

  /**
   * Назвать собеседника по-своему — на этом устройстве, и больше нигде. Не `setRoomName`:
   * это `m.room.name` в комнате на двоих, которое собеседник получит и прочтёт. Пустое —
   * вернуть настоящее имя.
   */
  async renameLocally(name: string | null): Promise<void> {
    if (this.direct) await this.localNames?.set(this.roomId, name);
  }

  rename(name: string): Promise<boolean> {
    const value = name.trim();
    if (value === this.serverName) return Promise.resolve(true);
    return this.act('rename', () => this.client.setRoomName(this.roomId, value));
  }

  setTopic(topic: string): Promise<boolean> {
    const value = topic.trim();
    if (value === this.topic) return Promise.resolve(true);
    return this.act('topic', () => this.client.setRoomTopic(this.roomId, value));
  }

  /** `mxc` — уже загруженная картинка; `null` — убрать. */
  setPicture(mxc: string | null): Promise<boolean> {
    return this.act('picture', () => this.client.sendStateEvent(this.roomId, EventType.RoomAvatar, (mxc ? { url: mxc } : {}) as never, ''));
  }

  setOpen(open: boolean): Promise<boolean> {
    if (open === this.open) return Promise.resolve(true);
    return this.act('visibility', () =>
      this.client.sendStateEvent(this.roomId, EventType.RoomJoinRules, { join_rule: open ? 'public' : 'invite' } as never, ''),
    );
  }

  invite(userId: string): Promise<boolean> {
    const id = userId.trim();
    if (!/^@[^:\s]+:\S+$/.test(id)) {
      this.failure = 'invite';
      return Promise.resolve(false);
    }
    return this.act('invite', () => this.client.invite(this.roomId, id));
  }

  remove(member: MemberSummary): Promise<boolean> {
    if (!this.mayRemove(member)) return Promise.resolve(false);
    return this.act('remove', () => this.client.kick(this.roomId, member.id));
  }

  ban(member: MemberSummary): Promise<boolean> {
    if (!this.mayBan(member)) return Promise.resolve(false);
    return this.act('ban', () => this.client.ban(this.roomId, member.id));
  }

  setRole(member: MemberSummary, role: RoomRole): Promise<boolean> {
    if (!this.rolesFor(member).includes(role)) return Promise.resolve(false);
    return this.act('role', () => this.client.setPowerLevel(this.roomId, member.id, ROLE_LEVEL[role]));
  }

  leave(): Promise<boolean> {
    return this.act('leave', () => this.client.leave(this.roomId));
  }

  setAlerts(alerts: RoomAlerts): Promise<boolean> {
    const previous = this.alerts;
    this.alerts = alerts;
    return this.act('alerts', async () => {
      const { remove, add } = alertsRules(alerts, this.roomId);
      const rules = this.client.pushRules?.global;
      for (const rule of remove) {
        const exists = rules?.[rule.kind]?.some((r) => r.rule_id === rule.ruleId);
        if (exists) await this.client.deletePushRule('global', rule.kind as never, rule.ruleId);
      }
      if (add) await this.client.addPushRule('global', add.kind as never, add.ruleId, add.body as never);
    }).then((ok) => {
      if (!ok) this.alerts = previous;
      return ok;
    });
  }

  /** Кто управляет чатом — администраторы и модераторы. */
  get staff(): MemberSummary[] {
    return this.members.filter((m) => m.role !== 'member' && !m.invited);
  }

  /**
   * Какие ступени можно поставить правилу. Пусто — правило не тронуть: оно выше моей ступени
   * (или менять роли нельзя вовсе), и экран показывает его как факт.
   */
  rolesForRule(rule: RoomRule): RoomRole[] {
    if (!this.permissions.canChangeRoles || (this.ruleLevels[rule] ?? 0) > this.ownAuthority) return [];
    return (['member', 'moderator', 'administrator'] as const).filter((role) => ROLE_LEVEL[role] <= this.ownAuthority);
  }

  /** Сменить, кому можно одно дело. Сразу на экране; сервер отказал — обратно. */
  setRule(rule: RoomRule, role: RoomRole): Promise<boolean> {
    if (!this.rules || this.rules[rule] === role || !this.rolesForRule(rule).includes(role)) return Promise.resolve(true);
    const before = this.rules;
    this.rules = { ...before, [rule]: role };
    const { key, event } = RULE_LEVELS[rule];
    const level = ROLE_LEVEL[role];
    const content = event
      ? { ...this.powers, events: { ...((this.powers['events'] ?? {}) as Record<string, unknown>), [event]: level } }
      : { ...this.powers, [key]: level };
    return this.act('rules', () => this.client.sendStateEvent(this.roomId, EventType.RoomPowerLevels, content as never, '')).then((ok) => {
      if (!ok) this.rules = before;
      return ok;
    });
  }

  /** Пустить обратно: запрет снят, а сам человек в чат не вернётся — его зовут заново. */
  unban(member: MemberSummary): Promise<boolean> {
    return this.act('unban', () => this.client.unban(this.roomId, member.id));
  }

  setHistory(history: HistoryAccess): Promise<boolean> {
    if (history === this.history) return Promise.resolve(true);
    const previous = this.history;
    this.history = history;
    return this.act('history', () =>
      this.client.sendStateEvent(this.roomId, EventType.RoomHistoryVisibility, { history_visibility: history } as never, ''),
    ).then((ok) => {
      if (!ok) this.history = previous;
      return ok;
    });
  }

  /** Сервер адресов этой комнаты — тот, что после двоеточия в моём ID. */
  get addressServer(): string {
    return this.me.slice(this.me.indexOf(':') + 1);
  }

  /** Имя адреса без `#` и сервера — что стоит в поле. */
  get addressName(): string {
    return this.alias ? this.alias.slice(1, this.alias.indexOf(':') > 0 ? this.alias.indexOf(':') : undefined) : '';
  }

  /**
   * Дать комнате адрес `#имя:сервер` или убрать его (пустое имя). Сначала адрес занимается на
   * сервере, потом записывается в комнату главным: наоборот в комнате оказался бы адрес, ведущий
   * в никуда или в чужую комнату. Занят — так и сказать. Старый адрес своего сервера отпускается.
   */
  async setAddress(typed: string): Promise<boolean> {
    const local = addressName(typed);
    const alias = local ? `#${local}:${this.addressServer}` : undefined;
    if (alias === this.alias) return true;
    const previous = this.alias;
    this.working = true;
    this.failure = null;
    try {
      if (alias) {
        try {
          await this.client.createAlias(alias, this.roomId);
        } catch (error) {
          this.failure = (error as { httpStatus?: number }).httpStatus === 409 ? 'addressTaken' : 'address';
          return false;
        }
      }
      await this.client.sendStateEvent(this.roomId, EventType.RoomCanonicalAlias, (alias ? { alias } : {}) as never, '');
      this.alias = alias;
      if (previous?.endsWith(`:${this.addressServer}`)) await this.client.deleteAlias(previous).catch(() => {});
      return true;
    } catch {
      this.failure = 'address';
      return false;
    } finally {
      this.working = false;
    }
  }

  /** Спросить сервер, в каталоге ли комната. Спрашивают только настройки. */
  async loadDirectory(): Promise<void> {
    try {
      const { visibility } = await this.client.getRoomDirectoryVisibility(this.roomId);
      this.inDirectory = visibility === 'public';
    } catch {
      this.inDirectory = undefined;
    }
  }

  setInDirectory(listed: boolean): Promise<boolean> {
    const previous = this.inDirectory;
    this.inDirectory = listed;
    return this.act('directory', () => this.client.setRoomDirectoryVisibility(this.roomId, listed ? Visibility.Public : Visibility.Private)).then((ok) => {
      if (!ok) this.inDirectory = previous;
      return ok;
    });
  }

  dismissFailure(): void {
    this.failure = null;
  }

  destroy(): void {
    for (const off of this.detach.splice(0)) off();
  }
}
